import React from 'react';
import FormField from '@amzn/awsui-components-core/form-field';
import Select from '@amzn/awsui-components-core/select';
import Multiselect from '@amzn/awsui-components-core/multiselect';
import TokenGroup from '@amzn/awsui-components-core/token-group';
import type { StudioOption } from 'src/components/star-grid/reportStudio.types';
import { useStudioMultiselect } from 'src/components/star-grid/hooks/useStudioMultiselect';
import styles from 'src/components/star-grid/components/styles/ReportStudio.module.scss';
interface Props {
  readonly label: string;
  readonly value: string;
  readonly options: readonly StudioOption[];
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly testId?: string;
  readonly selectedLabel?: string;
  /**
   * Render WITHOUT the FormField wrapper: no label block above and no stretched field.
   *
   * For the member editor's hosts, whose own toolbar dropdowns are bare `Select`s — a labelled FormField
   * there read as a separate full-width form row above the editor, with its label spacing between the two.
   * The label is kept as the accessible name so dropping it costs nothing to a screen reader.
   */
  readonly inline?: boolean;
}
export const StudioSelect: React.FC<Props> = ({ label, value, options, onChange, disabled, testId, selectedLabel, inline }) => {
  const select = (
    <Select
      data-testid={testId}
      filteringType="auto"
      expandToViewport
      disabled={disabled}
      ariaLabel={label}
      selectedOption={value ? { value, label: selectedLabel ?? options.find((item) => item.id === value)?.label ?? value } : null}
      options={options.map((item) => ({ value: item.id, label: item.label }))}
      onChange={({ detail }) => onChange(detail.selectedOption.value ?? '')}
    />
  );
  return inline ? select : <FormField label={label}>{select}</FormField>;
};
interface MultipleProps extends Omit<Props, 'value' | 'onChange' | 'selectedLabel'> {
  readonly values: readonly string[];
  readonly onChange: (values: readonly string[]) => void;
}
export const StudioMultiselect: React.FC<MultipleProps> = ({ label, values, options, onChange, disabled, testId }) => {
  const selection = useStudioMultiselect(values, options, onChange);
  return (
    <FormField label={label} className={styles.multiselectField}>
      <Multiselect
        data-testid={testId}
        filteringType="auto"
        expandToViewport
        hideTokens
        keepOpen
        dropdownRole="dialog"
        placeholder={selection.summary}
        filteringAriaLabel={selection.text('findValue')}
        filteringPlaceholder={selection.text('findValue')}
        noMatch={selection.text('noMatchingValues')}
        empty={selection.text('noFilterValues')}
        disabled={disabled}
        selectedOptions={selection.selectedOptions}
        options={selection.options}
        onChange={selection.onChange}
        renderDropdownHeader={() =>
          values.length > 0 && (
            <div className={styles.multiselectSelections} role="group" aria-label={selection.text('studioSelectedValues')}>
              <TokenGroup items={selection.tokens} alignment="vertical" disableOuterPadding readOnly={disabled} onDismiss={selection.onDismiss} />
            </div>
          )
        }
      />
    </FormField>
  );
};
